import React, { useEffect, useState } from "react";
import {
  Drawer,
  Input,
  Select,
  DatePicker,
  Button,
  Tag,
  Avatar,
  Upload,
  Popconfirm,
  Spin,
  message,
  Typography,
  Empty,
  Progress,
  Tabs,
  Badge,
} from "antd";
import {
  PaperClipOutlined,
  SendOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
  FormOutlined,
  CommentOutlined,
  AppstoreOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import moment from "moment";
import _auth from "auth";
import {
  ticketByIdService,
  ticketUpdateService,
  ticketDeleteService,
  ticketCommentCreateService,
  ticketCommentDeleteService,
  ticketAttachmentDeleteService,
  ticketUsersService,
} from "services/tickets";
import { handleApiError } from "utils";
import TicketBusy, { TicketProgressBar } from "./TicketAsync";
import { timeTrackerAssignableUserLabel } from "utils/time_tracker_assignable_user_label";
import {
  PRIORITIES,
  PRIORITY_META,
  STATUS_META,
  userDisplayName,
  userInitials,
  formatFileSize,
  normalizeId,
} from "../constants";

const { TextArea } = Input;
const { Text, Title } = Typography;
const { TabPane } = Tabs;

const DRAWER_TAB_STORAGE_KEY = "ticketDrawerActiveTab";
const DRAWER_TABS = ["details", "comments", "files"];

function getStoredDrawerTab() {
  try {
    const stored = localStorage.getItem(DRAWER_TAB_STORAGE_KEY);
    if (DRAWER_TABS.includes(stored)) return stored;
  } catch (e) {
    /* ignore */
  }
  return "comments";
}

function storeDrawerTab(tab) {
  try {
    localStorage.setItem(DRAWER_TAB_STORAGE_KEY, tab);
  } catch (e) {
    /* ignore */
  }
}

function getInitialTab(defaultTab) {
  if (defaultTab && DRAWER_TABS.includes(defaultTab)) return defaultTab;
  return getStoredDrawerTab();
}

export default function TicketDrawer({
  ticketId,
  open,
  onClose,
  onUpdated,
  canManage,
  embedded = false,
  defaultTab,
}) {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [users, setUsers] = useState([]);
  const [comment, setComment] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const [activeTab, setActiveTab] = useState(() => getInitialTab(defaultTab));

  const load = () => {
    if (!ticketId) return;
    setLoading(true);
    ticketByIdService(ticketId)
      .then((res) => {
        setTicket(res.data.item);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        handleApiError(err);
      });
  };

  useEffect(() => {
    if ((open || embedded) && ticketId) load();
    if (!open && !embedded) setUploadProgress(null);
  }, [open, embedded, ticketId]);

  useEffect(() => {
    if (defaultTab && DRAWER_TABS.includes(defaultTab)) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab, ticketId]);

  useEffect(() => {
    ticketUsersService()
      .then((res) => setUsers(res.data.items || []))
      .catch(() => {});
  }, []);

  const saveField = (patch) => {
    if (!canManage || saving) return;
    setSaving(true);
    ticketUpdateService(ticketId, patch)
      .then((res) => {
        setTicket((prev) => ({ ...prev, ...res.data.item }));
        onUpdated && onUpdated(res.data.item);
      })
      .catch(handleApiError)
      .finally(() => setSaving(false));
  };

  const submitComment = () => {
    if (!comment.trim()) return;
    setCommentLoading(true);
    ticketCommentCreateService({ ticket: normalizeId(ticketId), body: comment.trim() })
      .then((res) => {
        setTicket((prev) => ({
          ...prev,
          comments: [...(prev.comments || []), res.data.item],
          comment_count: (prev.comment_count || 0) + 1,
        }));
        setComment("");
        setCommentLoading(false);
        setActiveTab("comments");
        storeDrawerTab("comments");
        onUpdated && onUpdated();
      })
      .catch((err) => {
        setCommentLoading(false);
        handleApiError(err);
      });
  };

  const onDeleteTicket = () => {
    setDeleting(true);
    ticketDeleteService(ticketId)
      .then(() => {
        message.success("Ticket deleted");
        onClose(true);
      })
      .catch(handleApiError)
      .finally(() => setDeleting(false));
  };

  const uploadProps = {
    name: "file",
    action: process.env.REACT_APP_API_BASE_URL + "/ticket-attachment/upload",
    headers: { Authorization: "Bearer " + _auth.getToken() },
    data: { ticket: normalizeId(ticketId) },
    showUploadList: false,
    disabled: !canManage || !ticketId || !!uploadProgress,
    onChange(info) {
      const { file } = info;
      if (file.status === "uploading") {
        setUploadProgress({
          name: file.name,
          percent: file.percent != null ? Math.round(file.percent) : 0,
        });
        return;
      }
      if (file.status === "done") {
        setUploadProgress(null);
        message.success(file.response?.message || "File uploaded");
        setActiveTab("files");
        storeDrawerTab("files");
        load();
        onUpdated && onUpdated();
      } else if (file.status === "error") {
        setUploadProgress(null);
        const payload = file.response;
        handleApiError(
          payload
            ? { response: { status: payload.statusCode || 500, data: payload } }
            : { message: "Upload failed" }
        );
      }
    },
  };

  const onTabChange = (key) => {
    setActiveTab(key);
    storeDrawerTab(key);
  };

  const pm = ticket ? PRIORITY_META[ticket.priority] : null;
  const commentCount = ticket?.comments?.length || 0;
  const attachmentCount = ticket?.attachments?.length || 0;
  const projectId = ticket?.project && (ticket.project._id || ticket.project);
  const boardUrl = projectId
    ? `/tickets/projects/board/${normalizeId(projectId)}?ticket=${normalizeId(ticketId)}`
    : null;

  const detailsPanel = ticket && (
    <>
      <div className="ticket-drawer-section">
        <h4>Description</h4>
        <TextArea
          rows={4}
          value={ticket.description}
          readOnly={!canManage}
          onChange={(e) => setTicket({ ...ticket, description: e.target.value })}
          onBlur={(e) => {
            if (canManage) saveField({ description: e.target.value });
          }}
          placeholder="Add a detailed description…"
        />
      </div>

      <div className="ticket-drawer-section">
        <h4>Details</h4>
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <Text type="secondary">Priority</Text>
            <Select
              style={{ width: "100%", marginTop: 4 }}
              value={ticket.priority}
              disabled={!canManage}
              onChange={(v) => saveField({ priority: v })}
            >
              {PRIORITIES.map((p) => (
                <Select.Option key={p} value={p}>
                  {PRIORITY_META[p].label}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <Text type="secondary">Assignees</Text>
            <Select
              mode="multiple"
              style={{ width: "100%", marginTop: 4 }}
              value={(ticket.assignees || []).map((a) => a._id)}
              disabled={!canManage}
              onChange={(v) => saveField({ assignees: v })}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={[...users]
                .map((u) => ({
                  value: u._id,
                  label: timeTrackerAssignableUserLabel(u),
                }))
                .sort((a, b) =>
                  (a.label || "").localeCompare(b.label || "", undefined, {
                    sensitivity: "base",
                  })
                )}
            />
          </div>
          <div>
            <Text type="secondary">Due Date</Text>
            <DatePicker
              style={{ width: "100%", marginTop: 4 }}
              value={ticket.due_date ? moment(ticket.due_date) : null}
              disabled={!canManage}
              onChange={(d) => saveField({ due_date: d ? d.format("YYYY-MM-DD") : "" })}
            />
          </div>
          <div>
            <Text type="secondary">Labels</Text>
            <Select
              mode="tags"
              style={{ width: "100%", marginTop: 4 }}
              value={ticket.labels || []}
              disabled={!canManage}
              placeholder="Add labels"
              onChange={(v) => saveField({ labels: v })}
            />
          </div>
        </div>
      </div>

      {canManage && (
        <div className="ticket-drawer-section ticket-drawer-danger-zone">
          <Popconfirm title="Delete this ticket permanently?" onConfirm={onDeleteTicket}>
            <Button danger icon={<DeleteOutlined />} loading={deleting}>
              Delete Ticket
            </Button>
          </Popconfirm>
        </div>
      )}
    </>
  );

  const filesPanel = ticket && (
    <div className="ticket-drawer-section">
      {canManage && (
        <div className="ticket-upload-area">
          <Upload {...uploadProps}>
            <Button
              icon={<PaperClipOutlined />}
              loading={!!uploadProgress}
              disabled={!!uploadProgress}
            >
              {uploadProgress ? "Uploading…" : "Upload file or image"}
            </Button>
          </Upload>
          {uploadProgress && (
            <div className="ticket-upload-progress">
              <Text type="secondary" ellipsis title={uploadProgress.name}>
                Uploading {uploadProgress.name}
              </Text>
              <Progress
                percent={uploadProgress.percent}
                status="active"
                size="small"
                showInfo={uploadProgress.percent > 0}
              />
              {uploadProgress.percent === 0 && (
                <Text type="secondary" className="ticket-upload-progress-hint">
                  Preparing upload…
                </Text>
              )}
            </div>
          )}
        </div>
      )}
      {(ticket.attachments || []).length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No attachments" style={{ margin: "12px 0" }} />
      ) : (
        <div className="attachment-grid">
          {(ticket.attachments || []).map((att) => (
            <div key={att._id} className="attachment-thumb">
              {att.is_image && (att.thumbnail_url || att.file_url) ? (
                <a href={att.file_url} target="_blank" rel="noopener noreferrer">
                  <img src={att.thumbnail_url || att.file_url} alt={att.original_name} />
                </a>
              ) : (
                <div className="attachment-file">
                  <FileOutlined style={{ fontSize: 24, marginBottom: 4 }} />
                  <span>{att.original_name?.slice(0, 12)}…</span>
                  <Text type="secondary">{formatFileSize(att.file_size)}</Text>
                </div>
              )}
              <div style={{ position: "absolute", top: 4, right: 4, display: "flex", gap: 2 }}>
                <a href={att.file_url} target="_blank" rel="noopener noreferrer">
                  <Button type="primary" size="small" icon={<DownloadOutlined />} />
                </a>
                {canManage && (
                  <Popconfirm
                    title="Delete attachment?"
                    onConfirm={() => {
                      setDeletingAttachmentId(att._id);
                      ticketAttachmentDeleteService(att._id)
                        .then(() => {
                          load();
                          onUpdated && onUpdated();
                        })
                        .catch(handleApiError)
                        .finally(() => setDeletingAttachmentId(null));
                    }}
                  >
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={deletingAttachmentId === att._id}
                    />
                  </Popconfirm>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const commentsPanel = ticket && (
    <div className="ticket-drawer-comments">
      {canManage && (
        <div className="ticket-comment-compose">
          <TextArea
            rows={3}
            placeholder="Write a comment…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={commentLoading}
            onClick={submitComment}
          >
            Post Comment
          </Button>
        </div>
      )}

      <div className="ticket-comment-list">
        {(ticket.comments || []).length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No comments yet" />
        ) : (
          (ticket.comments || []).map((c) => (
            <div key={c._id} className="comment-item">
              <Avatar size="small" style={{ background: "#6366f1" }}>
                {userInitials(c.created_by)}
              </Avatar>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 4 }}>
                  <Text strong>{userDisplayName(c.created_by)}</Text>
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    {moment(c.created_at).format("MMM D, h:mm A")}
                  </Text>
                  {canManage && (
                    <Popconfirm
                      title="Delete comment?"
                      onConfirm={() => {
                        setDeletingCommentId(c._id);
                        ticketCommentDeleteService(c._id)
                          .then(() => load())
                          .catch(handleApiError)
                          .finally(() => setDeletingCommentId(null));
                      }}
                    >
                      <Button
                        type="link"
                        size="small"
                        danger
                        style={{ float: "right" }}
                        loading={deletingCommentId === c._id}
                      >
                        Delete
                      </Button>
                    </Popconfirm>
                  )}
                </div>
                <div className="comment-body">{c.body}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const drawerBusy =
    saving || deleting || commentLoading || !!uploadProgress || !!deletingCommentId || !!deletingAttachmentId;

  const ticketBody =
    loading || !ticket ? (
      <Spin style={{ display: "block", margin: embedded ? "80px auto" : "40px auto" }} />
    ) : (
      <div className="ticket-system">
        <TicketProgressBar active={drawerBusy} />
        <TicketBusy spinning={saving} tip="Saving changes…">
        {embedded && (
          <div className="ticket-view-header">
            <div>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => onClose && onClose(false)}
                style={{ marginLeft: -8, marginBottom: 4 }}
              >
                Back to tickets
              </Button>
              <Title level={3} style={{ marginBottom: 4 }}>
                {ticket.ticket_number}
              </Title>
              <Title level={4} editable={canManage ? { onChange: (v) => saveField({ title: v }) } : false} style={{ marginTop: 0 }}>
                {ticket.title}
              </Title>
            </div>
            {boardUrl && (
              <Button icon={<AppstoreOutlined />} onClick={() => history.push(boardUrl)}>
                View on board
              </Button>
            )}
          </div>
        )}

        {!embedded && (
          <Title level={4} editable={canManage ? { onChange: (v) => saveField({ title: v }) } : false}>
            {ticket.title}
          </Title>
        )}

        <div style={{ marginBottom: 12 }}>
          <Tag color={pm?.color}>{pm?.label}</Tag>
          <Tag color={STATUS_META[ticket.status]?.color}>{STATUS_META[ticket.status]?.label}</Tag>
          {embedded && ticket.project?.name && (
            <Tag color="blue">{ticket.project.name}</Tag>
          )}
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={onTabChange}
          className="ticket-drawer-tabs"
        >
          <TabPane
            tab={
              <span>
                <FormOutlined /> Details
              </span>
            }
            key="details"
          >
            {detailsPanel}
          </TabPane>
          <TabPane
            tab={
              <span>
                <CommentOutlined /> Comments
                {commentCount > 0 && (
                  <Badge count={commentCount} style={{ marginLeft: 6 }} size="small" />
                )}
              </span>
            }
            key="comments"
          >
            {commentsPanel}
          </TabPane>
          <TabPane
            tab={
              <span>
                <PaperClipOutlined /> Files
                {attachmentCount > 0 && (
                  <Badge count={attachmentCount} style={{ marginLeft: 6 }} size="small" />
                )}
              </span>
            }
            key="files"
          >
            {filesPanel}
          </TabPane>
        </Tabs>
        </TicketBusy>
      </div>
    );

  if (embedded) {
    return <div className="ticket-view-shell">{ticketBody}</div>;
  }

  return (
    <Drawer
      title={ticket ? ticket.ticket_number : "Ticket"}
      width={560}
      visible={open}
      onClose={() => onClose(false)}
      destroyOnClose
    >
      {ticketBody}
    </Drawer>
  );
}
